/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global DockableSideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends DockableSideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');
      }

      // [May 2026] Switched from direct cartIcon.addEventListener to
      // document-level event delegation. Previously: if the user clicked the
      // cart icon during an AJAX cart update (e.g. immediately after Add to
      // Cart), the bound listener sometimes failed to fire and the link
      // navigated to /cart. With delegation, the handler is on document and
      // always catches the click via bubbling — independent of cart-drawer
      // element lifecycle, section refreshes, or any innerHTML replacements.
      if (!CartDrawer._delegationBound) {
        CartDrawer._delegationBound = true;
        document.addEventListener('click', (evt) => {
          const target = evt.target && evt.target.closest && evt.target.closest('#cart-icon');
          if (!target) return;
          // [May 2026] Mobile bypass restored - mobile cart icon goes to /cart
          // page instead of opening drawer. Cart page has search, tab nav, and
          // works without the drawer's complex state management. Drawer kept
          // for desktop only (docked at 1100px+, slide-in below).
          if (window.innerWidth <= 768) return;
          const drawer = document.querySelector('cart-drawer');
          if (!drawer) return;
          if (!drawer.isDocked) {
            evt.preventDefault();
            drawer.open(target);
          }
        });
        document.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          const active = document.activeElement;
          if (!active || active.id !== 'cart-icon') return;
          if (window.innerWidth <= 768) return;
          const drawer = document.querySelector('cart-drawer');
          if (!drawer || drawer.isDocked) return;
          drawer.open(active);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      this.addEventListener('click', this.handleEmptyCart.bind(this));

      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refreshCartDrawer.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Clears the cart
     * @param {object} evt - Event object.
     */
    handleEmptyCart(evt) {
      if (evt.target.matches('.js-empty-cart')) {
        evt.preventDefault();

        // eslint-disable-next-line no-alert, no-restricted-globals
        if (confirm(theme.strings.confirmEmptyCart)) {
          this.querySelectorAll('.cart-item').forEach((cartItem, index) => {
            setTimeout(() => cartItem.classList.add('cart-item--out'), index * 50);
          });

          try {
            fetch(`${theme.routes.cartClear}.js`, {
              method: 'POST'
            }).then((response) => {
              if (!response.ok) throw new Error(response.status);

              // Broadcast individual events to update product cards
              this.querySelectorAll('.cart-item').forEach((cartItem) => {
                this.dispatchEvent(new CustomEvent('on:line-item:change', {
                  bubbles: true,
                  detail: {
                    variantId: cartItem.dataset.variantId,
                    newQuantity: 0
                  }
                }));
              });

              this.refreshCartDrawer();
            });
          } catch (error) {
            console.log('Error clearing cart:', error); // eslint-disable-line
            window.location.href = evt.target.href;
          }
        }
      }
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';

        // [May 2026] Force-trigger product-recommendations fetch on drawer open.
        // The lazy-load IntersectionObserver doesn't reliably fire on mobile
        // when the drawer first opens (element may not enter viewport, esp.
        // when cart contents fit without scrolling), so the "You may also
        // like" section never populates. Manually call init() if the element
        // is still empty.
        const recElement = this.querySelector('product-recommendations');
        if (recElement && !recElement.hasChildNodes() && typeof recElement.init === 'function') {
          recElement.init();
        }
      }, quickAddDrawer ? 200 : 0);
      window.initLazyImages();
    }

/**
 * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
 * drawer
 * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
 * a straight refresh of the whole cart
 */
async refreshCartDrawer(dontRefreshCartItems = false) {
  try {
    const cartItems = this.querySelector('cart-items[data-empty="false"]');

    if (
      cartItems &&
      !dontRefreshCartItems &&
      typeof cartItems.refreshCartItems === 'function'
    ) {
      await cartItems.refreshCartItems();
      return;
    }

    const response = this.getSectionsToRender().map((section) => section.section);
    const cartResponse = await fetch(`?sections=${response.join(',')}`);
    const sections = await cartResponse.json();
    this.renderContents({ sections }, false);
    CartDrawer.recalculateCssVarHeights();
  } catch (error) {
    console.log(error); // eslint-disable-line
    this.dispatchEvent(new CustomEvent('on:cart:error', {
      bubbles: true,
      detail: {
        error: this.errorMsg ? this.errorMsg.textContent : null
      }
    }));
  }
}

    /**
     * Renders the contents of the specified sections to update.
     *
     * Uses SURGICAL updates (cart items, footer, totals, bubble only) —
     * never replaces the entire <cart-drawer> innerHTML.  A full replace
     * destroys Avada Free Gifts' milestone bar and causes duplicate
     * widgets, misplaced elements ("A$NaN"), and re-inject loops.
     *
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      // Flag so external MutationObservers (Avada reinject) skip our updates
      window.__AP_SECTIONS_UPDATING__ = true;

      const drawerSectionId = this.closest('.shopify-section').id.replace('shopify-section-', '');
      const drawerHtml = data.sections[drawerSectionId];

      if (drawerHtml) {
        const drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');

        // [May 2026] OPTION B - wholesale content swap.
        // Replaces .drawer__content innerHTML with the freshly-rendered HTML
        // and refreshes .cart-sticky-checkout--drawer separately. The Avada
        // milestone bar lives OUTSIDE .drawer__content as a sibling at the
        // <cart-drawer> top level (.Avada-ProgressBarCartDrawer__BlockWrapper),
        // so it's automatically left untouched — no preservation needed.
        //
        // Earlier this code preserved .cart-drawer__summary thinking it was
        // the Avada bar. That actually preserved the order summary inside
        // .drawer__content, making subtotals/savings stale (e.g. qty 2
        // showing only the unit price as subtotal). Removed.

        const drawerContentEl = this.querySelector('.drawer__content');
        const newDrawerContentEl = drawerDoc.querySelector('.drawer__content');
        if (drawerContentEl && newDrawerContentEl) {
          // Capture line keys of currently-rendered freebies BEFORE the swap.
          // After swap, any row with the same data-key but no .cart-item--freebie
          // class is an orphan freebie that lost its discount (cart dropped
          // below threshold). Hide it instantly so the shopper doesn't see the
          // freebie at full price for ~1.5s while cartSync removes it.
          const freebieKeysBeforeSwap = new Set();
          drawerContentEl.querySelectorAll('.cart-item--freebie[data-key]').forEach((row) => {
            freebieKeysBeforeSwap.add(row.dataset.key);
          });

          // [May 2026] Preserve populated upsell ("You may also like") across
          // the swap to prevent flashing on every cart update. Save the live
          // <product-recommendations> element if it already has items, then
          // re-attach after innerHTML replacement. The recommendations are
          // based on the first paid product, which usually doesn't change
          // when qty/items are added or removed - so re-fetching every time
          // is wasted work and causes a visible flicker.
          const liveUpsell = drawerContentEl.querySelector('product-recommendations#cart-upsell-recommendations');
          let preservedUpsell = null;
          if (liveUpsell && liveUpsell.querySelector('.cart-upsell-compact__item')) {
            preservedUpsell = liveUpsell;
            if (preservedUpsell.parentNode) preservedUpsell.parentNode.removeChild(preservedUpsell);
          }

          drawerContentEl.innerHTML = newDrawerContentEl.innerHTML;

          // Restore preserved upsell into its slot in the new content.
          if (preservedUpsell) {
            const newUpsellSlot = drawerContentEl.querySelector('#cart-upsell-recommendations');
            if (newUpsellSlot) {
              newUpsellSlot.replaceWith(preservedUpsell);
            }
          }

          // Note: previously had a workaround here to manually init() cart-items
          // because it gated init() on data-empty="false". That gate was removed
          // in cart-items.js (May 2026), so this manual trigger is no longer
          // needed - listeners are attached on construction regardless of state.

          // Hide orphan freebies (was a freebie, now without freebie class).
          if (freebieKeysBeforeSwap.size) {
            drawerContentEl.querySelectorAll('.cart-item[data-key]').forEach((row) => {
              if (!freebieKeysBeforeSwap.has(row.dataset.key)) return;
              if (row.classList.contains('cart-item--freebie')) return;
              row.style.display = 'none';
            });
          }

          // Move stepper into .cart-item__pst-stepper SYNCHRONOUSLY before
          // the browser paints. Without this, there's a brief visible flash
          // of the old layout (stepper in .cart-item__qty cell, on its own
          // row) before moveSteppers in cart-items.liquid catches up via
          // MutationObserver/setTimeout. Doing the move inline in the same
          // execution context as the innerHTML swap eliminates the flash.
          drawerContentEl.querySelectorAll('.cart-item').forEach((row) => {
            const target = row.querySelector('.cart-item__pst-stepper');
            if (!target) return;
            if (target.querySelector('quantity-input')) return;
            const qtyCell = row.querySelector('.cart-item__qty');
            if (!qtyCell) return;
            const quantityInput = qtyCell.querySelector('quantity-input');
            if (quantityInput) target.appendChild(quantityInput);
          });

          // Sync layout classes (empty vs non-empty cart)
          const isEmpty = !!drawerContentEl.querySelector('.js-cart-empty');
          drawerContentEl.classList.toggle('drawer__content--flex', isEmpty);
          drawerContentEl.classList.toggle('drawer__empty', isEmpty);
          drawerContentEl.classList.toggle('drawer__empty-with-promotions',
            isEmpty && !!drawerContentEl.querySelector('.cart-drawer__promoted-products-container'));
          const innerContentEl = drawerContentEl.querySelector('.cart-drawer__content');
          if (innerContentEl) {
            innerContentEl.classList.toggle('grow', isEmpty);
            innerContentEl.classList.toggle('flex', isEmpty);
            innerContentEl.classList.toggle('items-center', isEmpty);
            innerContentEl.classList.toggle('justify-center', isEmpty);
            innerContentEl.classList.toggle('w-full', isEmpty);
          }

          // Trigger product-recommendations fetch on the upsell. After the
          // wholesale swap, the new <product-recommendations> element is
          // server-rendered without recommendation items (the cart-drawer
          // section render doesn't include recommendations context). Calling
          // init() makes the element fetch its data-url which DOES include
          // recommendations context.
          //
          // Use setTimeout(0) so the custom element is fully upgraded before
          // we call init() on it. product-recommendations.js is loaded by the
          // cart-upsell-carousel.liquid snippet so the custom element should
          // already be defined by the time the user adds an item.
          setTimeout(() => {
            const upsell = drawerContentEl.querySelector('product-recommendations#cart-upsell-recommendations');
            if (!upsell) return;
            // Skip if recommendations already rendered (has product items)
            if (upsell.querySelector('.cart-upsell-compact__item')) return;

            // [May 2026] Defensive: if the server-rendered upsell came back
            // with empty data-product-id (Avada middleware can interfere with
            // section render context), compute it client-side from the actual
            // cart-item rows in the drawer. Find the first non-freebie row's
            // product_id and patch the upsell element so init() can fetch.
            if (!upsell.dataset.productId) {
              const firstPaidRow = drawerContentEl.querySelector('.cart-item:not(.cart-item--freebie)[data-product-id]');
              if (firstPaidRow && firstPaidRow.dataset.productId) {
                const productId = firstPaidRow.dataset.productId;
                upsell.dataset.productId = productId;
                // Also patch data-url which has product_id= empty in the URL
                if (upsell.dataset.url) {
                  upsell.dataset.url = upsell.dataset.url.replace(/product_id=[^&]*/, 'product_id=' + productId);
                }
              }
            }

            if (typeof upsell.init === 'function') {
              upsell.init();
            } else {
              console.warn('[cart-drawer] product-recommendations custom element has no init method - product-recommendations.js may not be loaded');
            }
          }, 0);
        }

        // Sticky checkout bar (lives outside .drawer__content as a sibling)
        const stickyCur = this.querySelector('.cart-sticky-checkout--drawer');
        const stickyNew = drawerDoc.querySelector('.cart-sticky-checkout--drawer');
        if (stickyCur && stickyNew) {
          stickyCur.innerHTML = stickyNew.innerHTML;
        }
      }

      // 6) Cart icon bubble
      const bubbleHtml = data.sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const el = document.getElementById('cart-icon-bubble');
        if (el) {
          const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const wrapper = bubbleDoc.querySelector('.shopify-section');
          el.innerHTML = wrapper ? wrapper.innerHTML : bubbleHtml;

          // Update the drawer count from the bubble too
          const drawerCount = document.getElementById('cart-drawer__count');
          if (drawerCount) {
            const tmpl = document.createElement('template');
            tmpl.innerHTML = bubbleHtml;
            const countEl = tmpl.content.querySelector('.header__cart-count-number');
            if (countEl) drawerCount.innerHTML = `(${countEl.innerHTML})`;
          }
        }
      }

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      window.initLazyImages();
      CartDrawer.recalculateCssVarHeights();

      // Clear updating flag and notify listeners.
      // [May 2026] Reduced from 200ms → 0ms so listeners (moveSteppers, etc.)
      // run sooner. Previously the 200ms delay caused a visible window where
      // the cart-item layout was wrong (stepper not yet moved into the
      // combined price-stepper-total row).
      setTimeout(() => {
        window.__AP_SECTIONS_UPDATING__ = false;
        document.dispatchEvent(new CustomEvent('on:line-item:change', { bubbles: true }));
      }, 0);
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      const sections = [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];

      // Append any quick order lists to the sections
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections.push({
            id: `quick-order-list_${sectionId}`,
            section: sectionId,
            selector: `#quick-order-list_${sectionId}`
          });
        }
      });

      return sections;
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
